/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import { allCatalogNames } from '@field-catalog';

/**
 * Macro names the **driver** can actually resolve — now **projected from the catalog**
 * rather than hand-kept (`PF`, D10/D20).
 *
 * Separate from `MACRO_DEFAULTS`, which is a catalog of *suggested mappings* — a
 * much smaller, editorial list. Whether a macro resolves is a different question
 * from whether we recommend it, and answering the first with the second is why the
 * validator called `{app_store_url}` unknown while the driver resolved it: 38 names
 * judging a vocabulary of 98.
 *
 * This is the same failure the design review records for `{contentRating}` (#882) —
 * a frontend vocabulary drifting from what the driver actually sends. It cannot be
 * fixed by being careful, so `resolvable-macros.test.ts` reads the driver's own
 * `context-value-resolver.ts` and fails when the two disagree.
 *
 * **The hand-written list is gone.** It carried **31 names the resolver has no case for**
 * — `title`, `series`, `placement`, `origin` and the rest — so the editor reported *no
 * issue* on rows that went out empty. That is the worse of the two failures, and it is
 * what D24 names: *a missing macro is recoverable, an offered one that does not resolve is
 * not.* Deriving the set from `FIELD_CATALOG`, which is checked against the resolver in
 * both directions, is D24's trim applied wholesale rather than name by name.
 *
 * **Operator-visible change**: a row carrying one of those 31 now draws an *unknown macro*
 * warning. The row was already sending nothing; the warning is the editor stopping saying
 * otherwise, and it routes the author to the path form, which works. The **capability**
 * gap those names represented is tracked separately (#1091) — trimming discharges the
 * false clearance, never the missing home (D25).
 *
 * A false "unknown macro" warning is not a cosmetic problem: a validator that cries
 * wolf on two thirds of the vocabulary teaches operators to ignore warnings, which
 * costs the real ones.
 */
export const RESOLVABLE_MACRO_NAMES: ReadonlySet<string> = new Set(allCatalogNames());

/**
 * Normalise a macro token the way `context-value-resolver` does before it switches.
 *
 * Named `normalize`, not `canonical`: "canonical" is reserved in this codebase for *the
 * one name per concept*, and this is a string-shape rule (camelCase → snake), not a choice
 * between synonyms. Two senses of one word in one vocabulary module is exactly the
 * confusion the one-name-per-concept discipline exists to prevent.
 *
 * **Mirrors** `libs/drivers/context-value-resolver.ts` — the console cannot import
 * `libs/*` (separate Vite app, no project reference), so this is a copy, and
 * `resolvable-macros.test.ts` fails if it drifts. (This said
 * `scripts/macro-vocabulary.spec.ts` until 2026-08-17 — commit `26412796` deleted that file
 * when it moved the check here and left the pointer behind, routing the next reader to a
 * gate that is not there.)
 *
 * The rule it carries: a camelCase spelling collapses onto its snake form, so
 * `ipAddress` and `ip_address` are one name. Both call sites previously did a bare
 * `.toLowerCase()`, which turns `ipAddress` into `ipaddress` — a name in nobody's list —
 * so **`{ipAddress}`, `{userAgent}` and `{appStoreUrl}` were warned as unknown while the
 * driver resolved all three**. The editor offers those exact spellings in autocomplete,
 * so the warning fired on values it had just suggested.
 */
export function normalizeMacroName(name: string): string {
  return name
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase();
}

/**
 * Will the driver resolve this macro token? **The** answer — both the parameter and the
 * header validator ask through here, so the two surfaces cannot disagree about whether a
 * given `{token}` is known.
 */
export function isResolvableMacro(token: string): boolean {
  return RESOLVABLE_MACRO_NAMES.has(normalizeMacroName(token));
}
